import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, Truck } from 'lucide-react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

export default function VehicleNew() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    immatriculation: '',
    marque: '',
    modele: '',
    annee: new Date().getFullYear(),
    type: 'Camion',
    kilometrage: 0,
    statut: 'ok',
    date_assurance: '',
    date_controle_technique: '',
    date_prochain_entretien: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      // Clean empty dates
      const dataToSend = { ...formData };
      if (!dataToSend.date_assurance) delete dataToSend.date_assurance;
      if (!dataToSend.date_controle_technique) delete dataToSend.date_controle_technique;
      if (!dataToSend.date_prochain_entretien) delete dataToSend.date_prochain_entretien;
      
      await api.post('/vehicles', dataToSend);
      toast.success('Véhicule créé avec succès');
      navigate('/vehicles');
    } catch (error) {
      console.error('Error creating vehicle:', error);
      const errorMsg = error.response?.data?.detail;
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Erreur lors de la création du véhicule');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="space-y-6 p-4 md:p-0 pb-24 md:pb-0" data-testid="vehicle-new-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="outline"
          onClick={() => navigate('/vehicles')}
          className="bg-zinc-800/50 border-white/10 text-white hover:bg-white/10"
          data-testid="back-button"
        >
          <ArrowLeft size={18} className="mr-2" />
          Retour
        </Button>
        <div className="flex items-center gap-3">
          <div className="p-2 bg-red-500/20 rounded-lg">
            <Truck size={24} className="text-red-400" />
          </div>
          <div>
            <h2 className="text-2xl font-semibold text-white">Nouveau Véhicule</h2>
            <p className="text-zinc-400">Ajoutez un véhicule à la flotte</p>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="glass-panel p-6 max-w-3xl">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Section Identification */}
          <div>
            <h3 className="text-lg font-semibold mb-4 pb-2 border-b border-white/10 text-white">Identification</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="immatriculation" className="text-zinc-300">Immatriculation *</Label>
                <Input
                  id="immatriculation"
                  value={formData.immatriculation}
                  onChange={(e) => handleChange('immatriculation', e.target.value.toUpperCase())}
                  placeholder="AB-123-CD"
                  required
                  className="mt-1 bg-zinc-800/50 border-white/10 text-white"
                  data-testid="input-immatriculation"
                />
              </div>
              <div>
                <Label htmlFor="type" className="text-zinc-300">Type de véhicule *</Label>
                <Select
                  value={formData.type}
                  onValueChange={(value) => handleChange('type', value)}
                >
                  <SelectTrigger className="mt-1 bg-zinc-800/50 border-white/10 text-white" data-testid="select-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-800 border-white/10">
                    <SelectItem value="Camion">Camion</SelectItem>
                    <SelectItem value="Camionnette">Camionnette</SelectItem>
                    <SelectItem value="Utilitaire">Utilitaire</SelectItem>
                    <SelectItem value="Semi-remorque">Semi-remorque</SelectItem>
                    <SelectItem value="Fourgon">Fourgon</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="marque" className="text-zinc-300">Marque *</Label>
                <Input
                  id="marque"
                  value={formData.marque}
                  onChange={(e) => handleChange('marque', e.target.value)}
                  placeholder="Mercedes, Renault, Iveco..."
                  required
                  className="mt-1 bg-zinc-800/50 border-white/10 text-white"
                  data-testid="input-marque"
                />
              </div>
              <div>
                <Label htmlFor="modele" className="text-zinc-300">Modèle *</Label>
                <Input
                  id="modele"
                  value={formData.modele}
                  onChange={(e) => handleChange('modele', e.target.value)}
                  placeholder="Actros, T High..."
                  required
                  className="mt-1 bg-zinc-800/50 border-white/10 text-white"
                  data-testid="input-modele"
                />
              </div>
              <div>
                <Label htmlFor="annee" className="text-zinc-300">Année *</Label>
                <Input
                  id="annee"
                  type="number"
                  min="2000"
                  max={new Date().getFullYear() + 1}
                  value={formData.annee}
                  onChange={(e) => handleChange('annee', parseInt(e.target.value))}
                  required
                  className="mt-1 bg-zinc-800/50 border-white/10 text-white"
                  data-testid="input-annee"
                />
              </div>
              <div>
                <Label htmlFor="kilometrage" className="text-zinc-300">Kilométrage *</Label>
                <Input
                  id="kilometrage"
                  type="number"
                  min="0"
                  value={formData.kilometrage}
                  onChange={(e) => handleChange('kilometrage', parseInt(e.target.value) || 0)}
                  placeholder="0"
                  required
                  className="mt-1 bg-zinc-800/50 border-white/10 text-white"
                  data-testid="input-kilometrage"
                />
              </div>
            </div>
          </div>

          {/* Section Statut */}
          <div>
            <h3 className="text-lg font-semibold mb-4 pb-2 border-b border-white/10 text-white">Statut</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="statut" className="text-zinc-300">Statut actuel *</Label>
                <Select
                  value={formData.statut}
                  onValueChange={(value) => handleChange('statut', value)}
                >
                  <SelectTrigger className="mt-1 bg-zinc-800/50 border-white/10 text-white" data-testid="select-statut">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-800 border-white/10">
                    <SelectItem value="ok">OK - Opérationnel</SelectItem>
                    <SelectItem value="a_surveiller">À surveiller</SelectItem>
                    <SelectItem value="en_maintenance">En maintenance</SelectItem>
                    <SelectItem value="immobilise">Immobilisé</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Section Dates importantes */}
          <div>
            <h3 className="text-lg font-semibold mb-4 pb-2 border-b border-white/10 text-white">Dates importantes</h3>
            <div className="flex flex-wrap justify-center gap-6">
              <div className="flex flex-col items-center">
                <Label htmlFor="date_assurance" className="text-zinc-300 text-center">Date d'assurance</Label>
                <Input
                  id="date_assurance"
                  type="date"
                  value={formData.date_assurance}
                  onChange={(e) => handleChange('date_assurance', e.target.value)}
                  className="mt-1 bg-zinc-800/50 border-white/10 text-white [&::-webkit-calendar-picker-indicator]:invert"
                  data-testid="input-assurance"
                />
              </div>
              <div className="flex flex-col items-center">
                <Label htmlFor="date_controle_technique" className="text-zinc-300 text-center">Contrôle technique</Label>
                <Input
                  id="date_controle_technique"
                  type="date"
                  value={formData.date_controle_technique}
                  onChange={(e) => handleChange('date_controle_technique', e.target.value)}
                  className="mt-1 bg-zinc-800/50 border-white/10 text-white [&::-webkit-calendar-picker-indicator]:invert"
                  data-testid="input-controle"
                />
              </div>
              <div className="flex flex-col items-center">
                <Label htmlFor="date_prochain_entretien" className="text-zinc-300 text-center">Prochain entretien</Label>
                <Input
                  id="date_prochain_entretien"
                  type="date"
                  value={formData.date_prochain_entretien}
                  onChange={(e) => handleChange('date_prochain_entretien', e.target.value)}
                  className="mt-1 bg-zinc-800/50 border-white/10 text-white [&::-webkit-calendar-picker-indicator]:invert"
                  data-testid="input-entretien"
                />
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end gap-3 pt-4 border-t border-white/10">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/vehicles')}
              className="bg-zinc-800/50 border-white/10 text-white hover:bg-white/10"
            >
              Annuler
            </Button>
            <Button
              type="submit"
              disabled={loading}
              variant="brand"
              data-testid="submit-vehicle"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Création...
                </span>
              ) : (
                <>
                  <Save size={18} className="mr-2" />
                  Créer le véhicule
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
